import React, {
    JSX,
    forwardRef,
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    ColorValue,
    LayoutChangeEvent,
    LayoutRectangle,
    Platform,
    ViewStyle,
} from 'react-native';

import Animated, {
    AnimatedStyleProp,
    SharedValue,
} from 'react-native-reanimated';

import debounce from 'lodash.debounce';

import Container from './Container';
import DataProvider from './DataProvider';
import ItemContainer from './ItemContainer';
import { LayoutProvider } from './LayoutProvider';
import VirtualRenderer, {
    RenderStack,
    RenderStackItem,
} from './VirtualRenderer';
import { CellItem, Layout } from './types';

type Props = {
    rowHeight: number;
    layoutProvider: LayoutProvider;
    dataProvider: DataProvider;
    rowRenderer: (
        data: CellItem,
        layout: Layout,
    ) => JSX.Element | JSX.Element[] | null;
    Skeleton: JSX.Element | JSX.Element[] | null;
    renderAheadOffsetHorizontal: number;
    renderAheadOffsetVertical: number;
    onTouchStart?: () => void;
    onVerticalScroll?: (offsetY: number) => void;
    onHorizontalScroll?: (offsetX: number) => void;
    onIndexesChanged?: (visible: number[][], engaged: number[][]) => void;
    onLayout?: (e: LayoutChangeEvent) => void;
    contentHeight: number;
    contentWidth: number;
    backgroundColor: ColorValue;
    verticalScrollEnabled?: boolean;
    horizontalScrollEnabled?: boolean;
    verticalScrollViewRef: React.Ref<Animated.ScrollView>;
    horizontalScrollViewRef: React.Ref<Animated.ScrollView>;
    verticalScrollStyle?: AnimatedStyleProp<ViewStyle>;
    horizontalScrollStyle?: AnimatedStyleProp<ViewStyle>;
    yScrollOffset: SharedValue<number>;
    skipYScrollOffsetUpdate: SharedValue<boolean>;
    xScrollOffset: SharedValue<number>;
    skipXScrollOffsetUpdate: SharedValue<boolean>;
    decelerationRate?: number;
    id: string;
    dataSet?: { class: string };
};

export interface IRecyclerGridView {
    reset: () => void;
    setTVPreferredFocusViewId: (id?: string) => void;
}

const RecyclerGridView = forwardRef<IRecyclerGridView, Props>(
    (
        {
            rowHeight,
            layoutProvider,
            dataProvider,
            rowRenderer,
            Skeleton,
            renderAheadOffsetHorizontal,
            renderAheadOffsetVertical,
            onTouchStart,
            onVerticalScroll,
            onHorizontalScroll,
            onIndexesChanged,
            onLayout,
            contentHeight,
            contentWidth,
            backgroundColor,
            verticalScrollEnabled,
            horizontalScrollEnabled,
            verticalScrollViewRef,
            horizontalScrollViewRef,
            verticalScrollStyle,
            horizontalScrollStyle,
            yScrollOffset,
            skipYScrollOffsetUpdate,
            xScrollOffset,
            skipXScrollOffsetUpdate,
            decelerationRate,
            id,
            dataSet,
        },
        ref,
    ) => {
        const [renderStack, setRenderStack] = useState<RenderStack>({});
        const virtualRenderer = useRef(
            new VirtualRenderer(
                rowHeight,
                renderAheadOffsetHorizontal,
                renderAheadOffsetVertical,
                setRenderStack,
            ),
        );

        const xOffset = useRef(0);
        const yOffset = useRef(0);

        const tvPreferredFocusViewId = useRef<string>(undefined);

        const onLayoutDebounced = useRef(
            debounce((layout: LayoutRectangle) => {
                const { width, height } = layout;
                if (height === 0 || width === 0) {
                    return;
                }

                virtualRenderer.current.setWindowSize({ width, height });
            }, 100),
        );

        const verticalContentContainerStyle = useMemo(
            () => ({
                height: contentHeight,
            }),
            [contentHeight],
        );

        const horizontalContentContainerStyle = useMemo(
            () => ({
                width: contentWidth,
                ...(Platform.OS === 'web' && {
                    overflow: 'clip' as ViewStyle['overflow'],
                }),
            }),
            [contentWidth],
        );

        const _onLayout = useCallback(
            (e: LayoutChangeEvent) => {
                onLayout?.(e);
                onLayoutDebounced.current(e.nativeEvent.layout);
            },
            [onLayout],
        );

        const _onHorizontalScroll = useCallback(
            (x: number) => {
                xOffset.current = x;
                virtualRenderer.current.updateOffset(
                    xOffset.current,
                    yOffset.current,
                );

                onHorizontalScroll?.(x);
            },
            [onHorizontalScroll],
        );

        const _onVerticalScroll = useCallback(
            (y: number) => {
                yOffset.current = y;
                virtualRenderer.current.updateOffset(
                    xOffset.current,
                    yOffset.current,
                );

                onVerticalScroll?.(y);
            },
            [onVerticalScroll],
        );

        const renderItem = useCallback(
            (key: string, item: RenderStackItem): JSX.Element | null => {
                const { row, index } = item;

                const data = dataProvider.getItem(row, index);
                const layout = layoutProvider.getLayout(row, index);

                let hasTVPreferredFocus = false;
                if (data.id === tvPreferredFocusViewId.current) {
                    hasTVPreferredFocus = true;
                    tvPreferredFocusViewId.current = undefined;
                }

                return (
                    <ItemContainer
                        key={key}
                        data={data}
                        layout={layout}
                        backgroundColor={backgroundColor}
                        childRenderer={rowRenderer}
                        hasTVPreferredFocus={hasTVPreferredFocus}
                    />
                );
            },
            [dataProvider, layoutProvider, backgroundColor, rowRenderer],
        );

        const Content = useMemo((): Array<JSX.Element | null> => {
            const renderedItems: (JSX.Element | null)[] = [];
            Object.entries(renderStack).forEach(([key, value]) => {
                renderedItems.push(renderItem(key, value));
            });

            return renderedItems;
        }, [renderStack, renderItem]);

        useLayoutEffect(() => {
            virtualRenderer.current.onIndexesChanged = onIndexesChanged;
        }, [onIndexesChanged]);

        useLayoutEffect(() => {
            virtualRenderer.current.getStableId = dataProvider.getStableId;
        }, [dataProvider.getStableId]);

        useLayoutEffect(() => {
            virtualRenderer.current.setLayouts(layoutProvider.getLayouts());
        }, [layoutProvider.getLayouts]);

        useImperativeHandle(
            ref,
            () => ({
                reset: () => {
                    virtualRenderer.current.reset();
                    setRenderStack({});
                },
                setTVPreferredFocusViewId: (id) => {
                    tvPreferredFocusViewId.current = id;
                },
            }),
            [],
        );

        return (
            <Container
                verticalScrollEnabled={verticalScrollEnabled}
                horizontalScrollEnabled={horizontalScrollEnabled}
                verticalScrollViewRef={verticalScrollViewRef}
                horizontalScrollViewRef={horizontalScrollViewRef}
                yScrollOffset={yScrollOffset}
                skipYScrollOffsetUpdate={skipYScrollOffsetUpdate}
                xScrollOffset={xScrollOffset}
                skipXScrollOffsetUpdate={skipXScrollOffsetUpdate}
                verticalScrollStyle={verticalScrollStyle}
                verticalContentContainerStyle={verticalContentContainerStyle}
                horizontalScrollStyle={horizontalScrollStyle}
                horizontalContentContainerStyle={
                    horizontalContentContainerStyle
                }
                decelerationRate={decelerationRate}
                onLayout={_onLayout}
                onTouchStart={onTouchStart}
                onVerticalScroll={_onVerticalScroll}
                onHorizontalScroll={_onHorizontalScroll}
                Skeleton={Skeleton}
                id={id}
                dataSet={dataSet}
            >
                {Content}
            </Container>
        );
    },
);

export default RecyclerGridView;
